import { Timestamp } from 'firebase/firestore';

export enum BadgeType {
    ADMIN = "ADMIN",
    CHAMPION = "CHAMPION",
    SUPPORTER = "SUPPORTER",
    PASSPORT = "PASSPORT"
}

export enum BadgeStatus {
    ACTIVE = "ACTIVE",
    INACTIVE = "INACTIVE"
}

export interface UserBadge {
    id?: string;
    userId: string;
    badgeType: BadgeType;
    description: string;
    createdAt: Date;
    status: BadgeStatus;
    order: number;
}

export interface Location {
    id: string;
    parentId: string;
    name: string;
    city: string;
    lat: string;
    lng: string;
    postcode: string;
    country: string;
    address: string;
    addressLine1: string;
    county: string;
    airport: boolean;
    hotel: boolean;
    holidayPark: boolean;
    isUniversity: boolean;
    trainStation: boolean;
    pubIsClosed: boolean;
    pubIsTemporaryClosed: boolean;
    openingDate?: string;
    closingDate?: string;
    visitCount: number;
    stayCount?: number;
    sessionUniqueCount: number;
    ratingCountLow: number;
    ratingCountHigh: number;
    lastModified: Date;
    visited: boolean;
    rating: number;
    enabled: boolean | undefined;
    bonus: boolean | undefined;
    likeCount: number;
    tags?: Array<string>;
}

export interface MessageChannels {
    lastSeen: Date,
    generalLastSeen: Date,
    supportLastSeen: Date,
}

export interface UserStats {
    [key: string]: number | undefined;
    statsVersion?: number;
    comments?: number;
    likes?: number;
    photos?: number;
    visits?: number;
    stays?: number;
    checkIns?: number;
    uniqueCheckIns?: number;
    checkInTime?: number;
    points?: number;
    pointsTotal?: number;
}

export interface User {
    authId: string;
    id: string;
    name: string;
    email: string;
    photoURL: string;
    admin: boolean;
    chatAdmin: boolean;
    userAdmin: boolean;
    photoAdmin: boolean;
    supporter: boolean;
    champion: number;
    passport: boolean;
    staff?: boolean;
    chatEnabled: boolean | undefined;
    photoEnabled: boolean | undefined;
    chatPushNotificationsEnabled: boolean;
    countUpdateEnabled: boolean;
    visitCount: number;
    stayCount?: number;
    photoCount: number;
    socialDisabled: boolean;
    badgeDisabled: boolean;
    sessionCount: number;
    sessionVerifiedCount: number;
    bonusPoints: number;
    messageChannels: MessageChannels;
    hideVisitedClosedPubsOnMap: boolean;
    enableUiV2?: boolean;
    lastSeenAt?: Date | undefined;
    status?: string;
    stats?: UserStats;
    countriesVisited?: Array<string>;
    badges?: Array<UserBadge>;
}

export interface Visit {
    userId: string;
    locationId: string;
    visited: boolean;
    lastModified: Date;
    verified: boolean;
}

export interface Stay {
    userId: string;
    locationId: string;
    stayed: boolean;
    lastModified: Date;
}

export interface Rating {
    userId: string;
    locationId: string;
    rating: number;
    lastModified: Date;
}

export interface HttpResult {
    status: number;
}

export interface NewsItem {
    id: string;
    userId: string;
    name: string;
    locationId: string;
    locationName: string;
    payload: string;
    likeCount: number;
    type: string;
    subType: string;
    lastModified: Date;
    verified: boolean;
    hidden: boolean | undefined;
}

export interface Comment {
    locationId: string;
    locationName: string;
    payload: string;
    likeCount: number;
    lastModified: Date;
}

export interface CommentInfo {
    commentId: string;
}

export interface Session {
    id: string;
    userId: string;
    name: string;
    locationId: string;
    locationName: string;
    checkInTime: Date;
    checkOutTime: Date;
    bonus: boolean | undefined;
}

export interface Points {
    amount: number;
    userId: string;
    locationId: string;
    recordedAt: Timestamp;
}

export interface PointsAccrual {
    userId: string,
    locationId: string,
    locationName: string,
    timeCheckedIn: number | undefined,
    points: number,
    isUnique: boolean,
    isBonus: boolean,
    recordedAt: Date,
}

export interface Stats {
    activeUsers: number;
    hoursCheckedIn: number;
    lastUpdated: Date;
    recentUsers: number;
    sessions: number;
    mostSimultaneousCheckIns?: number;
    mostSimultaneousCheckInsAt?: Date;
    comments: number;
    visits: number;
    stays: number;
    messages: number;
    photos: number;
}

export enum EmojiType {
    HEART = 'HEART',
    LAUGH = 'LAUGH',
    SHOCKED = 'SHOCKED',
    CRY = 'CRY',
    ANGER = 'ANGER',
    THUMB_UP = 'THUMB_UP',
    THUMB_DOWN = 'THUMB_DOWN'
}

export interface MessageReactions {
    [userId: string]: EmojiType;
}

export interface Message {
    id: string;
    payload: string;
    userName: string;
    userId: string;
    createdAt: Date;
    updatedAt: Date;
    reaction0Count: number;
    channel: string;
    reactions?: MessageReactions;
    hidden?: boolean;
}

export interface Notifications {
    lastMessage: Date;
    lastGeneralMessage: Date;
    lastSupportMessage: Date;
}

export interface SettingsDoc {
    chatEnabled: boolean;
    photoEnabled: boolean;
}

export interface Photo {
    id: string;
    fullSizeId: string;
    smallSizeId: string;
    userId: string;
    userName: string;
    locationId: string;
    locationName: string;
    fullSizeUrl: string;
    smallSizeUrl: string;
    hidden: boolean;
    tags: Array<string>;
    description?: string;
    createdAt: Date;
    updatedAt: Date;
    likeCount: number;
}

export interface NearestPub {
    locationId: string,
    locationName: string,
    distance: number,
}

export enum EventType {
    LOCATION_OPENING = "LOCATION_OPENING",
    LOCATION_CLOSING = "LOCATION_CLOSING",
    LOCATION_TEMPORARILY_CLOSING = "LOCATION_TEMPORARILY_CLOSING",
    LOCATION_REOPENING = "LOCATION_REOPENING",
    VISIT = "VISIT"
}

export interface Event {
    id: string;
    userId: string;
    createdAt: Date;
    scheduledAt: Date;
    eventTitle: string;
    eventType: EventType;
    eventDescription?: string;
    locationId?: string;
    eventCompleted: boolean;
}
